<template>
    <div :ref="id" :id="id" class="h-100"></div>
</template>

<script>
import * as d3 from "d3";
import * as _ from 'lodash';
import { getFunctionGetWFString, getWSString } from "@/services/data-tools.js";
import { months } from "@/services/entity-model.js";

export default {
     name: "HydroChart",
     props: {
         id: String,
         structure: Object,
         hasAxisLeft: {
             type: Boolean,
             default: true
         },
         tooltipPosition: {
             type: String,
             default: 'auto'
         },
         tooltipHeight: Number,
         tooltipWidth: Number
     },
     data() {
         return {
             chart: {},
             floor: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
             hoverObjects: []
         }
     },
     mounted(){
        this.renderHydroGraph();
     },
     methods:{
         renderHydroGraph() {
            var margin = this.structure.margin || { top: 10, right: 200, bottom: 20, left: 100 },
            width = this.$refs[this.id].clientWidth - margin.left - margin.right,
            height = this.$refs[this.id].clientHeight - margin.top - margin.bottom;
            this.chart.height = height;
            this.chart.width = width;

            const yMax = this.getMaxValue();
            d3.select("#svg-" + this.id).remove();
            this.chart.svg = d3.select("#" + this.id).append("svg")
                .attr("id", "svg-" + this.id)
                .attr("width", width + margin.left + margin.right)
                .attr("height", height + margin.top + margin.bottom)
                .append("g")
                    .attr("id", "chartarea-" + this.id)
                    .attr("transform",
                        "translate(" + margin.left + "," + margin.top + ")");

            var xScale = d3.scaleLinear()
                .domain([-0.5, 11.5])
                .range([0, width])

            this.chart.xScale = xScale;

            this.chart.ovewViewWidth = xScale(2) - xScale(1);

            var yScale = d3
                .scaleLinear()
                .domain([0, yMax])
                .range([height, 0]);

            if(this.hasAxisLeft) {
                this.chart.svg.append("g").call(d3.axisLeft(yScale)
                    .ticks(9)
                    .tickFormat(() => ''))
                    .selectAll('g')
                    .append("svg:foreignObject")
                        .attr("width",100)
                        .attr("height",20)
                        .attr("x", -110)
                        .attr("y", -8)
                    .append("xhtml:div")
                        .html(d =>  {
                            return `<div class="chart-axis-label">${getFunctionGetWFString("mon")(d)}</div>`
                            });
            }

            this.chart.yScale = yScale;

            this.chart.svg.append("g")
                .attr("id", this.id + "-labels")
                .attr("transform", "translate(0," + height + ")")
                .call(d3.axisBottom(xScale).tickFormat((d, i) => this.structure.columns[i]))
                .selectAll("text")
                .style("font-size", 12)
                .style("font-weight", 200);

            this.chart.valueLine = d3.line()
                .x(function(d, i) {
                    return xScale(i);
                })
                .y((d) => {
                    return yScale(d);
                });

            this.chart.svg.selectAll(".background")
                .data([...Array(12).keys()])
                .enter()
                .append("rect")
                .attr("x", (d) => d * this.chart.ovewViewWidth)
                .attr("y", 0)
                .attr("width", this.chart.ovewViewWidth)
                .attr("height", height)
                .attr("stroke", "grey")
                .attr("fill", "white")
                .attr("opacity", 0.5);

            this.structure.areas.forEach(area => {
                this.drawArea(this.getAreaCoords(this.structure.lines[area.top].data, this.structure.lines[area.bottom].data), area.color);
            });


            for(let lineName in this.structure.lines) {
                const line = this.structure.lines[lineName];
                if (line.label != null) {
                    this.drawLine(this.getLineCoords(line.data), line.color, line.stroke);
                }
            }

            this.hoverObjects = [];
            for(let lineName in this.structure.lines) {
                const line = this.structure.lines[lineName];
                if (line.label != null) {
                    this.hoverObjects.push({
                        name: lineName,
                        polyline: this.chart.svg.append("polyline")
                            .style("fill", "none")
                            .attr("stroke", line.color)
                            .style("stroke-width", "1px"),
                        point: this.chart.svg.append('circle')
                            .attr("cx", 10)
                            .attr("cy", 10)
                            .attr("r", 4)
                            .attr("fill", "white")
                            .attr("stroke-width", 2)
                            .attr("stroke", line.color)
                            .attr("opacity", 0)
                    })
                }
            }

            this.chart.overviewRect = this.chart.svg.append('rect')
                .attr("x", 0)
                .attr("y", 0)
                .attr("width", this.chart.ovewViewWidth)
                .attr("height", height)
                .attr("fill", "#f5f5f5")
                .attr("opacity", 0);

            this.chart.tooltip = this.chart.svg
                .append("foreignObject")
                .attr("width", this.tooltipWidth)
                .attr("height", this.tooltipHeight)
                .attr("opacity", this.structure.defaultTooltip ? 1 : 0)
                .attr("x", this.chart.width / 2  - this.tooltipWidth / 2)
                .attr("y", this.chart.height + 30);

            this.chart.tooltip
                .append("xhtml:div")
                .style("font", "12px 'Helvetica Neue'")
                .html(() => `<div class="chart-tooltip">${this.structure.defaultTooltip || ""}</div>`
            );

            this.chart.svg
                    .on("mousemove", (d, i, nodes) => {
                        var coordinates= d3.mouse(nodes[i]);
                        this.onMouseOver(coordinates);
                    })
                    .on("mouseout", () => this.onMouseOut());
        },
        onMouseOver(coordinates) {
            const index = Math.floor(coordinates[0] / this.chart.ovewViewWidth) < 0 ? 0 : Math.floor(coordinates[0] / this.chart.ovewViewWidth);
            if(index > 11) return;

            this.chart.overviewRect.attr("opacity", 0)
            this.chart.overviewRect.attr("x", index * this.chart.ovewViewWidth);
            this.hoverObjects.forEach(ho => {
                ho.point.attr("opacity", 1);
                ho.point.attr("cx", this.chart.xScale(index));
                ho.point.attr("cy", this.chart.yScale(this.structure.lines[ho.name].data[index]));
            });
            this.chart.tooltip.attr("opacity", 1);
            this.updateTooltip(coordinates, index);
        },
        onMouseOut() {
            this.chart.overviewRect.attr("opacity", 0)
            this.hoverObjects.forEach(ho => {
                ho.point.attr("opacity", 0);
                ho.polyline.attr("opacity", 0);
            });

            if(this.structure.defaultTooltip) {
                this.chart.tooltip.html(() => `<div class="chart-tooltip">${this.structure.defaultTooltip}</div>`);
            } else {
                this.chart.tooltip.attr("opacity", 0)
            }
        },
        updateTooltip(coordinates, index) {
            const tooltipXDistance = 80;
            let tooltipY = 0;
            let tooltipX = 0;
            if(this.tooltipPosition == 'auto') {
                tooltipY = coordinates[1] > this.chart.height - 200 ? this.chart.height - 200 : coordinates[1];
                tooltipX = this.chart.xScale(index) + tooltipXDistance;
            } else {
                tooltipY = this.chart.height + 30;
                tooltipX = this.chart.width / 2  - this.tooltipWidth / 2;
            }

            this.chart.tooltip.attr("x", tooltipX);
            this.chart.tooltip.attr("y", tooltipY);

            this.chart.tooltip.html(() => {
                let html =`<div class="chart-tooltip"><p><strong>${months[index].long}</strong><p>`;
                let blueWS = 1;
                for (let lineName in this.structure.lines) {
                    const line = this.structure.lines[lineName];
                    if (line.label != null) {
                        if (lineName == "blueFootprints") {
                            blueWS *= line.data[index];
                        } else if (lineName == "blueAvailabilities") {
                            blueWS /= line.data[index];
                        }
                        html += `<p>${line.label}<br/>${getFunctionGetWFString("mon")(line.data[index])}</p>`;
                    }
                }
                html += `<p>Blue Water Scarcity<br/>${getWSString(blueWS.toPrecision(2))}</p></div>`;
                return html;
            });

            this.hoverObjects.forEach((ho, i) => {
                ho.polyline.attr("points", () => {
                    if(this.tooltipPosition == 'auto') {
                        const point = [parseFloat(ho.point.attr("cx")) + 4, ho.point.attr("cy")];
                        const innerPoint1 = [this.chart.xScale(index) + 30 + i * 20, point[1]];
                        const innerPoint2 = [innerPoint1[0], tooltipY + 60 + i * 70];
                        const tooltipCoordinates = [this.chart.xScale(index) + tooltipXDistance, innerPoint2[1]];
                        return [point, innerPoint1, innerPoint2, tooltipCoordinates];
                    } else {
                        const point = [parseFloat(ho.point.attr("cx")) + 4, ho.point.attr("cy")];
                        const innerPoint1 = [tooltipX - 30 - i * 20, point[1]];
                        const innerPoint2 = [tooltipX - 30 - i * 20, tooltipY + 60 + i * 70];
                        const tooltipCoordinates = [tooltipX, innerPoint2[1]];
                        return [point, innerPoint1, innerPoint2, tooltipCoordinates];
                    }
                });
                ho.polyline.attr("opacity", 1);
            });
        },
        drawArea(data, color) {
            this.chart.svg.append("path")
                .datum(data)
                .attr("fill", color)
                .attr("stroke", "white")
                .attr("stroke-width", 1)
                .attr("d", d3.area()
                    .x(d => this.chart.xScale(d.x))
                    .y0(d => this.chart.yScale(d.y1))
                    .y1(d => this.chart.yScale(d.y0))
                );
        },
        drawLine(data, color, stroke) {
            this.chart.svg.append("path")
                .datum(data)
                .attr("fill", "none")
                .attr("stroke", 'white')
                .attr("stroke-width", 5)
                .attr("d", d3.line()
                    .x(d => this.chart.xScale(d.x))
                    .y(d => this.chart.yScale(d.y))
                );

            this.chart.svg.append("path")
                .datum(data)
                .attr("fill", "none")
                .attr("stroke", color)
                .attr("stroke-width", stroke)
                .attr("d", d3.line()
                    .x(d => this.chart.xScale(d.x))
                    .y(d => this.chart.yScale(d.y))
                );
        },
        getLineCoords(a1) {
            const coords = _.zipWith(_.range(12), a1, (x, y) => ({x, y}));
            const startEndYear = { x: null, y: (coords[11].y + coords[0].y) / 2 };
            coords.splice(0, 0, Object.assign({}, startEndYear, {x: -0.5}));
            coords.push(Object.assign({}, startEndYear, {x: 11.5}));

            return coords;
        },
        getAreaCoords(a1, a2) {
            const coords = _.zipWith(_.range(12), a1, a2, (x, y0, y1) => ({x, y0, y1}));
            const startEndYear = {
                x: null,
                y0: (coords[11].y0 + coords[0].y0) / 2,
                y1: (coords[11].y1 + coords[0].y1) / 2
            };

            coords.splice(0, 0, Object.assign({}, startEndYear, {x: -0.5}));
            coords.push(Object.assign({}, startEndYear, {x: 11.5}));

            const intersectingCoords = [];
            for (let i = 0; i < coords.length; i++) {
                if (i < coords.length - 1) {
                    if ((coords[i].y1 - coords[i].y0) * (coords[i + 1].y1 - coords[i + 1].y0) < 0) {
                        const intersection = this.getIntersectionXY(
                        this.getLinearAB(coords[i].x, coords[i].y0, coords[i + 1].x, coords[i + 1].y0),
                        this.getLinearAB(coords[i].x, coords[i].y1, coords[i + 1].x, coords[i + 1].y1)
                        );
                        intersectingCoords.push({ x: intersection.x, y0: intersection.y, y1: intersection.y });
                    }
                }

                if (coords[i].y0 > coords[i].y1) {
                    coords[i].y0 = coords[i].y1;
                }
            }

            return coords.concat(intersectingCoords).sort((a, b) => (a.x >= b.x)? 1: (a.x == b.x)? 0: -1);
        },
        getIntersectionXY(linearK, linearL) {
            const x = (linearL.b - linearK.b) / (linearK.a - linearL.a);
            const y = linearK.a * x + linearK.b;
            return {x: x, y: y};
        },
        getLinearAB(leftX, leftY, rightX, rightY) {
            const a = (rightY - leftY) / (rightX - leftX);
            const b = leftY - a * leftX;
            return {a: a, b: b};
        },
         getMaxValue() {
             let max = 0;
             for(let lineName in this.structure.lines) {
                 const line = this.structure.lines[lineName];
                 if (line.label != null) {
                     const tmpMax = _.max(line.data);
                     max = tmpMax > max ? tmpMax : max;
                 }
             }

             return max;
         }
    },
    watch: {
        "structure": function() {
            this.renderHydroGraph();
        }
  }
}
</script>

<style lang="scss" scoped>
.areaBlue {
    fill: #B0D9FF;
}

.areaGreen {
    fill: #A4BB06;
}

.areaYellow {
    fill: #EEEE00;
}

.areaOrange {
    fill: #E89F00;
}

.areaDarkRed {
    fill: #DD0000;
}
polygon {
      fill: black;
}

.overview {
    fill: lightgrey;
    opacity: 0.5;
}

</style>
